// Primary Order (field-app) — a mapped sales user (ASM/RSM/ZSM/SALES_HEAD/
// MIS — anyone with the PRIMARY_ORDER workflow perm) punching a primary
// order (distributor → Tulsi Tea) ON BEHALF of one of their mapped
// distributors. Backend: routes_portal.py.
//
// * Pricing — the server prices every line with the distributor's PRIMARY
//   price list (`_effective_price(..., list_type="PRIMARY")`), separate
//   from secondary/retailer pricing. GET /primary-orders/orderable-skus
//   also returns a qty=1 preview of that same price so this screen can
//   show a live running total before submit; the authoritative amount is
//   still computed server-side per line at the actual entered qty.
// * All mapped SKUs are listed up front (respecting the distributor's
//   primary-order SKU allowlist/window) — no manual "add line" picker.
// * A distributor's OWN submission lands in status "placed" and needs the
//   mapped ASM/manager to Accept it (POST .../accept) before it's final;
//   an ASM/manager placing it themselves auto-approves immediately, same
//   as before.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Truck, ArrowLeft, Search, CheckCircle2, Download, Pencil, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { authenticatedDownload } from "@/components/bulk/useBulkJob";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import UomSelector, { defaultUomFor } from "@/components/UomSelector";

function newIdem() {
  return `po-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 });

// "approved" is the raw status this screen's own Accept action writes —
// shown to the user as "Accepted" to match the wording everywhere else in
// the app (admin Orders grid groups accepted/approved under one "Accepted"
// label already). Only a display concern; the stored value stays as-is.
const STATUS_LABELS = { approved: "Accepted" };
function statusLabel(status) {
  return STATUS_LABELS[status] || status;
}

const STATUS_COLORS = {
  booked:     "bg-amber-50 text-amber-700 border-amber-200",
  placed:     "bg-amber-50 text-amber-700 border-amber-200",
  approved:   "bg-sky-50 text-sky-700 border-sky-200",
  accepted:   "bg-sky-50 text-sky-700 border-sky-200",
  dispatched: "bg-indigo-50 text-indigo-700 border-indigo-200",
  delivered:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  fulfilled:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial_fulfilled: "bg-amber-50 text-amber-800 border-amber-200",
  rejected:   "bg-rose-50 text-rose-700 border-rose-200",
  cancelled:  "bg-rose-50 text-rose-700 border-rose-200",
};

// Mirrors backend routes_uoms.py::convert_to_canonical — for the live cart
// summary only; the server re-derives this authoritatively on submit.
function toCanonical(sku, uom, enteredQty) {
  const q = Number(enteredQty) || 0;
  if (!sku || q <= 0) return { qtyPcs: 0, qtyKg: 0 };
  if (uom === "KG") {
    const w = sku.weight_per_pc_kg;
    if (!w) return { qtyPcs: 0, qtyKg: 0 };
    return { qtyPcs: Math.round(q / w), qtyKg: q };
  }
  if (uom === "BAG") {
    const f = sku.bag_conversion_factor;
    if (!f) return { qtyPcs: 0, qtyKg: 0 };
    const qtyPcs = Math.round(q * f);
    return { qtyPcs, qtyKg: qtyPcs * (sku.weight_per_pc_kg || 0) };
  }
  if (uom === "BUNCH") {
    const f = sku.bunch_conversion_factor;
    if (!f) return { qtyPcs: 0, qtyKg: 0 };
    const qtyPcs = Math.round(q * f);
    return { qtyPcs, qtyKg: qtyPcs * (sku.weight_per_pc_kg || 0) };
  }
  const qtyPcs = Math.round(q);
  return { qtyPcs, qtyKg: qtyPcs * (sku.weight_per_pc_kg || 0) };
}

// A SKU can now carry MORE than one live scheme at once (scheme_hints, an
// array from routes_portal.py's _scheme_details) — every function below
// works per-hint and the plural wrappers fan that out across all of them,
// so a SKU that qualifies for two independent schemes shows both instead
// of only the first one the backend happened to list.
function _hintsFor(sku) {
  if (Array.isArray(sku.scheme_hints)) return sku.scheme_hints;
  // Older cached responses (or any spot that hasn't refetched) only have
  // the flat singular fields — synthesize a single-item list from those.
  if (!sku.scheme_kind) return [];
  return [{
    scheme_id: sku.scheme_id, scheme_code: sku.scheme_code,
    kind: sku.scheme_kind, buy_qty_kg: sku.scheme_buy_qty_kg,
    buy_qty_pcs: sku.scheme_buy_qty_pcs, unit_qty: sku.scheme_unit_qty,
    unit_label: sku.scheme_unit_label, amount_per_unit: sku.scheme_amount_per_unit,
    unit_has_number: sku.scheme_unit_has_number,
  }];
}

// Mirrors backend routes_orders.py::_apply_scheme_to_line's FREE_GOODS
// multiple-of-buy_qty math, using one structured scheme hint — so the entry
// form can show what a SKU's CURRENT entered quantity actually earns (not
// just the static per-threshold hint), live as the rep types. Returns null
// when the entered qty hasn't hit this hint's threshold.
function _rewardForHint(hint, qtyKg, qtyPcs) {
  if (!hint || !hint.kind) return null;
  const threshold = hint.buy_qty_kg > 0 ? hint.buy_qty_kg : hint.buy_qty_pcs;
  const have = hint.buy_qty_kg > 0 ? qtyKg : qtyPcs;
  if (!threshold || !have) return null;
  const mult = Math.floor(have / threshold);
  if (mult <= 0) return null;
  if (hint.kind === "discount") {
    const amt = mult * (hint.amount_per_unit || 0);
    return { mult, kind: hint.kind, scheme_id: hint.scheme_id, scheme_code: hint.scheme_code,
             text: `₹${amt.toLocaleString("en-IN")} off` };
  }
  // Mirrors backend routes_orders.py::_apply_scheme_to_line's two free_item_
  // note scaling shapes: a leading number scales directly ("2 Kg Jeera");
  // plain text with none (a fixed-value description like "Rs.100/- Free
  // Pkt" that has nothing to multiply) gets an "N ×" prefix instead.
  if (hint.kind === "free_item_note" && hint.unit_has_number === false) {
    return { mult, kind: hint.kind, scheme_id: hint.scheme_id, scheme_code: hint.scheme_code,
             text: mult > 1 ? `${mult} × ${hint.unit_label || ""}` : (hint.unit_label || "") };
  }
  const qty = mult * (hint.unit_qty || 0);
  const qtyStr = Number.isInteger(qty) ? String(qty) : qty.toFixed(2).replace(/\.?0+$/, "");
  return { mult, kind: hint.kind, scheme_id: hint.scheme_id, scheme_code: hint.scheme_code,
           // unit_qty/unit_label kept alongside the already-scaled `text` so
           // freeItemTotals can group by the per-threshold reward ("1 Kg
           // Jeeru") and sum `mult` across lines, instead of re-parsing the
           // scaled number back out of the display text.
           unit_qty: hint.unit_qty, unit_label: hint.unit_label,
           text: `${qtyStr} ${hint.unit_label || ""}`.trim() };
}

// All live rewards a SKU has earned at its current entered quantity — one
// entry per matching scheme, in scheme_hints order.
function liveSchemeRewards(sku, qtyKg, qtyPcs) {
  return _hintsFor(sku).map((h) => _rewardForHint(h, qtyKg, qtyPcs)).filter(Boolean);
}

// Back-compat single-reward accessor for any spot that only cares about
// the first matching scheme.
function liveSchemeReward(sku, qtyKg, qtyPcs) {
  return liveSchemeRewards(sku, qtyKg, qtyPcs)[0] || null;
}

// "Add N more kg to unlock the next tier" — a nudge shown whether or not
// the current qty has already crossed a threshold, so a rep who's just
// short of the next multiple (e.g. 47kg with a 24kg step) can round the
// order up instead of leaving a reward on the table. Simulates the reward
// at the next threshold by reusing _rewardForHint's own math rather than
// duplicating it. One gap hint per scheme hint.
function nextTierGaps(sku, qtyKg, qtyPcs) {
  const out = [];
  for (const hint of _hintsFor(sku)) {
    const kgBased = hint.buy_qty_kg > 0;
    const threshold = kgBased ? hint.buy_qty_kg : hint.buy_qty_pcs;
    if (!threshold) continue;
    const have = kgBased ? (qtyKg || 0) : (qtyPcs || 0);
    const mult = Math.floor(have / threshold);
    const remainder = have - mult * threshold;
    if (remainder <= 0) continue; // already exactly on a tier boundary
    const gap = Math.round((threshold - remainder) * 100) / 100;
    const nextReward = _rewardForHint(
      hint,
      kgBased ? qtyKg + gap : qtyKg,
      kgBased ? qtyPcs : qtyPcs + gap,
    );
    if (!nextReward) continue;
    out.push({ gap, unit: kgBased ? "kg" : "pcs", reward: nextReward });
  }
  return out;
}

// Back-compat single-gap accessor.
function nextTierGap(sku, qtyKg, qtyPcs) {
  return nextTierGaps(sku, qtyKg, qtyPcs)[0] || null;
}

// A free_item_note reward's text carries its count one of two ways:
// - "N × <label>" for a fixed-value giveaway ("Rs.100/- Free Pkt") that has
//   nothing of its own to scale — N is an explicit multiplier prefix.
// - "<N> <label>" for a quantity-based giveaway ("12 Kg Jeera") where the
//   backend has already scaled the leading number by how many times the
//   threshold was hit — that leading number IS the count.
// Plain text with neither shape implies a count of 1. Splitting count from
// label lets totals for the same label sum correctly instead of the whole
// scaled string ("12 Kg Jeera") being treated as a one-off label with an
// implied count of 1.
function _parseFreeItemNote(text) {
  const byTimes = /^(\d+)\s*×\s*(.+)$/.exec(text);
  if (byTimes) return [Number(byTimes[1]), byTimes[2]];
  const byLeadingNumber = /^([\d.]+)\s+(.+)$/.exec(text);
  if (byLeadingNumber) return [Number(byLeadingNumber[1]), byLeadingNumber[2]];
  return [1, text];
}

// Grouping by label and summing lets the review screen show one running
// total per distinct giveaway across every SKU (and every scheme on that
// SKU) that earned it, instead of the rep adding up separate lines
// themselves.
function freeItemTotals(cartLines) {
  const totals = new Map();
  for (const l of cartLines) {
    for (const r of l.rewards || []) {
      if (r.kind !== "free_item_note") continue;
      // Quantity-based reward (unit_qty set by _rewardForHint): group by the
      // per-threshold reward itself ("1 Kg Jeeru"), not the already-scaled
      // text ("12 Kg Jeeru"), and sum `mult` — the number of times that
      // per-threshold reward was earned — across lines.
      if (r.unit_qty != null) {
        const unitQtyStr = Number.isInteger(r.unit_qty) ? String(r.unit_qty) : r.unit_qty.toFixed(2).replace(/\.?0+$/, "");
        const label = `${unitQtyStr} ${r.unit_label || ""}`.trim();
        totals.set(label, (totals.get(label) || 0) + r.mult);
        continue;
      }
      const [count, label] = _parseFreeItemNote(r.text);
      totals.set(label, (totals.get(label) || 0) + count);
    }
  }
  return [...totals.entries()];
}

// Same aggregation, over a placed order's own lines (scheme_benefit_type/
// _value, the field names the order-detail response uses) — for the
// post-submit detail dialog, which reads the authoritative booked amounts
// rather than the live pre-submit preview.
function freeItemTotalsFromOrderLines(lines) {
  const totals = new Map();
  for (const l of lines || []) {
    if (l.scheme_benefit_type !== "free_item_note" || !l.scheme_benefit_value) continue;
    const [count, label] = _parseFreeItemNote(l.scheme_benefit_value);
    totals.set(label, (totals.get(label) || 0) + count);
  }
  return [...totals.entries()];
}

export default function PrimaryOrderField() {
  const navigate = useNavigate();
  const { hasWorkflowPerm, user } = useAuth();
  const allowed = hasWorkflowPerm("PRIMARY_ORDER");
  const canAuto = hasWorkflowPerm("AUTO_PRIMARY");   // per-user Auto Primary toggle (admin decides)
  // Accept is a manager-level verification step — SE is a leaf field role
  // and must never see (or be able to hit) it, even if PRIMARY_ORDER was
  // manually granted to them so they could place an order. Backend also
  // enforces this — see accept_primary_order in routes_portal.py.
  const canAccept = user?.role_code !== "SE" && user?.role_code !== "DISTRIBUTOR";

  const [mappedDistributors, setMappedDistributors] = useState(null); // null = loading
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  // List filters — date range, distributor, and who placed it.
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterDistId, setFilterDistId] = useState("__all__");
  const [filterUserId, setFilterUserId] = useState("__all__");
  // Populated from whatever's been loaded so far — every distinct placer
  // seen becomes a User-filter option, without a separate lookup endpoint.
  const [knownUsers, setKnownUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [distributorId, setDistributorId] = useState("");
  const [skuInfo, setSkuInfo] = useState(null); // {items, in_window, ...} | null loading
  const [entries, setEntries] = useState({}); // sku_id -> {uom, qty}
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [viewOrder, setViewOrder] = useState(null); // {..., lines, status_log} | null
  const [viewLoading, setViewLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    api.get("/distributors", { params: { scope: "mapped", page_size: 200 } })
      .then((r) => setMappedDistributors(r.data.items || []))
      .catch(() => setMappedDistributors([]));
  }, [allowed]);

  const loadOrders = () => {
    setOrdersLoading(true);
    const params = { page_size: 50 };
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    if (filterDistId !== "__all__") params.distributor_id = filterDistId;
    if (filterUserId !== "__all__") params.user_id = filterUserId;
    api.get("/primary-orders", { params })
      .then((r) => {
        const items = r.data.items || [];
        setOrders(items);
        setKnownUsers((prev) => {
          const byId = new Map(prev.map((u) => [u.id, u]));
          for (const o of items) {
            const uid = o.placed_by_user_id || o.user_id;
            if (uid && o.placed_by_user_name) byId.set(uid, { id: uid, name: o.placed_by_user_name });
          }
          return Array.from(byId.values());
        });
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load primary orders"))
      .finally(() => setOrdersLoading(false));
  };
  useEffect(() => { if (allowed) loadOrders(); }, [allowed, filterDateFrom, filterDateTo, filterDistId, filterUserId]);

  // Phase 15 · "Auto Order" — pre-fills every line with its suggested qty
  // (trailing 3-month avg secondary sales minus this month's so far) the
  // moment the SKU list loads, instead of requiring the distributor to tap
  // "Fill all with suggested qty" themselves after opening a blank form.
  // `autoOrderMode` carries the intent across the distributor picker for a
  // multi-mapped login, where the SKU fetch only fires after they pick one.
  const [autoOrderMode, setAutoOrderMode] = useState(false);

  const loadSkusFor = (distId, autoFill = false) => {
    setSkuInfo(null);
    setEntries({});
    setShowReview(false);
    api.get("/primary-orders/orderable-skus", { params: { distributor_id: distId } })
      .then((r) => {
        setSkuInfo(r.data);
        const init = {};
        let filled = 0;
        for (const s of r.data.items || []) {
          const useSuggested = autoFill && s.suggested_qty_kg > 0 && (s.allowed_uoms || []).includes("KG");
          if (useSuggested) filled += 1;
          init[s.sku_id] = useSuggested
            ? { uom: "KG", qty: String(s.suggested_qty_kg) }
            : { uom: defaultUomFor(s, { excludePcs: true }), qty: "" };
        }
        setEntries(init);
        if (autoFill) {
          toast[filled > 0 ? "success" : "info"](
            filled > 0
              ? `Auto-filled ${filled} SKU${filled === 1 ? "" : "s"} with suggested qty — review, add/remove, then place.`
              : "No suggested quantities yet for this distributor — add lines manually.");
        }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail?.message || "Failed to load orderable SKUs");
        setSkuInfo({ items: [] });
      });
  };

  const openForm = (autoFill = false) => {
    const only = mappedDistributors?.length === 1 ? mappedDistributors[0].id : "";
    setEditingOrderId(null);
    setAutoOrderMode(autoFill);
    setDistributorId(only);
    setSearch("");
    setNotes("");
    if (only) loadSkusFor(only, autoFill);
    else setSkuInfo(null);
    setShowForm(true);
  };

  const onPickDistributor = (v) => {
    setDistributorId(v);
    loadSkusFor(v, autoOrderMode);
  };

  const setEntry = (skuId, field, v) => {
    setEntries((e) => ({ ...e, [skuId]: { ...e[skuId], [field]: v } }));
  };

  const filteredSkus = useMemo(() => {
    const items = skuInfo?.items || [];
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((s) =>
      (s.sku_code || "").toLowerCase().includes(q) || (s.name || "").toLowerCase().includes(q));
  }, [skuInfo, search]);

  const cart = useMemo(() => {
    const items = skuInfo?.items || [];
    let totalKg = 0, totalAmount = 0, lineCount = 0;
    const lines = [];
    for (const s of items) {
      const en = entries[s.sku_id];
      if (!en || !Number(en.qty) || Number(en.qty) <= 0) continue;
      const { qtyPcs, qtyKg } = toCanonical(s, en.uom, en.qty);
      if (qtyPcs <= 0) continue;
      lineCount += 1;
      totalKg += qtyKg;
      const lineAmount = qtyPcs * (s.unit_price || 0);
      totalAmount += lineAmount;
      lines.push({ sku: s, qtyKg, qtyPcs, lineAmount, rewards: liveSchemeRewards(s, qtyKg, qtyPcs) });
    }
    return { totalKg, totalAmount, lineCount, lines,
             schemeCount: lines.filter((l) => l.rewards.length > 0).length };
  }, [skuInfo, entries]);

  const submit = async () => {
    if (!distributorId) { toast.error("Select a distributor"); return; }
    const items = skuInfo?.items || [];
    const payloadLines = [];
    for (const s of items) {
      const en = entries[s.sku_id];
      if (!en || !Number(en.qty) || Number(en.qty) <= 0) continue;
      payloadLines.push({ sku_id: s.sku_id, entered_uom: en.uom, entered_qty: Number(en.qty) });
    }
    if (payloadLines.length === 0) { toast.error("Enter a quantity for at least one SKU"); return; }
    setSubmitting(true);
    try {
      let orderId;
      if (editingOrderId) {
        await api.patch(`/primary-orders/${editingOrderId}/lines`, { lines: payloadLines });
        toast.success("Primary order updated");
        orderId = editingOrderId;
      } else {
        const { data } = await api.post("/primary-orders", {
          lines: payloadLines,
          notes: notes || null,
          for_distributor_id: distributorId,
          client_ts: new Date().toISOString(),
          idempotency_key: newIdem(),
        });
        toast.success("Primary order placed");
        orderId = data.id;
      }
      setShowForm(false);
      setShowReview(false);
      setEditingOrderId(null);
      loadOrders();
      // Straight into the detail dialog — that's also where "Download PDF"
      // lives, so placing/updating an order flows right into sharing it.
      openDetail(orderId);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || detail?.error || "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = (orderId) => {
    setViewLoading(true);
    setViewOrder({ id: orderId });
    api.get(`/primary-orders/${orderId}`)
      .then((r) => setViewOrder(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load order");
        setViewOrder(null);
      })
      .finally(() => setViewLoading(false));
  };

  const acceptOrder = async (orderId) => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/primary-orders/${orderId}/accept`);
      toast.success("Primary order accepted");
      setViewOrder((v) => (v && v.id === orderId ? { ...v, ...data } : v));
      loadOrders();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || detail?.error || "Accept failed"));
    } finally {
      setAccepting(false);
    }
  };

  const [rejecting, setRejecting] = useState(false);
  const [rejectReasonOpen, setRejectReasonOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const rejectOrder = async (orderId) => {
    if (!rejectReason.trim() || rejectReason.trim().length < 3) {
      toast.error("Enter a reason (min 3 characters)"); return;
    }
    setRejecting(true);
    try {
      const { data } = await api.post(`/primary-orders/${orderId}/reject`, { reason: rejectReason.trim() });
      toast.success("Primary order rejected");
      setViewOrder((v) => (v && v.id === orderId ? { ...v, ...data } : v));
      setRejectReasonOpen(false);
      setRejectReason("");
      loadOrders();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || detail?.error || "Reject failed"));
    } finally {
      setRejecting(false);
    }
  };

  // Editing reuses the same "all mapped SKU + qty" form as creating a new
  // order — just locked to the existing distributor and prefilled with the
  // current line quantities — and submits via PATCH .../lines instead of
  // POST, re-pricing every line fresh against today's PRIMARY price list.
  const [editingOrderId, setEditingOrderId] = useState(null);
  const openEditForm = (order) => {
    const distId = order.for_distributor_id || order.distributor_id;
    setEditingOrderId(order.id);
    setDistributorId(distId);
    setSearch("");
    setNotes(order.notes || "");
    setViewOrder(null);
    setSkuInfo(null);
    setEntries({});
    setShowReview(false);
    api.get("/primary-orders/orderable-skus", { params: { distributor_id: distId } })
      .then((r) => {
        setSkuInfo(r.data);
        const init = {};
        for (const s of r.data.items || []) {
          const existingLine = (order.lines || []).find((l) => l.sku_id === s.sku_id);
          init[s.sku_id] = existingLine
            ? { uom: existingLine.entered_uom || defaultUomFor(s, { excludePcs: true }), qty: String(existingLine.entered_qty ?? "") }
            : { uom: defaultUomFor(s, { excludePcs: true }), qty: "" };
        }
        setEntries(init);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail?.message || "Failed to load orderable SKUs");
        setSkuInfo({ items: [] });
      });
    setShowForm(true);
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async (order) => {
    setPdfBusy(true);
    try {
      // Same generic order-PDF endpoint the secondary-order screen uses —
      // it already handles primary_to_company orders fine (retailer_id is
      // just null, so that section of the PDF is omitted).
      await authenticatedDownload(`/orders/${order.id}/pdf`, `${order.order_no}.pdf`);
    } catch (e) {
      toast.error(`PDF download failed: ${e?.message || e}`);
    } finally {
      setPdfBusy(false);
    }
  };

  if (!allowed) {
    return (
      <div className="p-4 pb-24 space-y-3" data-testid="primary-order-field-denied">
        <button onClick={() => navigate(-1)} className="text-sm text-neutral-500 flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="text-sm text-neutral-500 text-center py-10">
          You don't have permission to place primary orders.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-3" data-testid="primary-order-field-page">
      <div className="space-y-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-400">
            Primary order
          </div>
          <h1 className="text-xl font-extrabold text-neutral-900">
            Distributor → Company
          </h1>
        </div>
        {/* Stacked below the title (not squeezed alongside it) — at phone
            width the title alone takes most of a side-by-side row, wrapping
            "New primary order" into 3 lines inside its own pill. */}
        {mappedDistributors && mappedDistributors.length > 0 && (
          <div className="flex gap-2">
            {canAuto && (
              <button
                onClick={() => openForm(true)}
                data-testid="primary-field-auto-order-btn"
                className="flex-1 inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg border border-[#E5197F]/40 text-[#E5197F] text-xs font-medium whitespace-nowrap"
              >
                <Sparkles className="w-4 h-4 shrink-0" /> Auto Order
              </button>
            )}
            <button
              onClick={() => openForm(false)}
              data-testid="primary-field-new-btn"
              className="flex-1 inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg bg-[#E5197F] text-white text-xs font-medium whitespace-nowrap"
            >
              <Truck className="w-4 h-4 shrink-0" /> New primary order
            </button>
          </div>
        )}
      </div>

      <div className="text-[11px] text-neutral-500 bg-sky-50 border border-sky-200 rounded p-2">
        Punch a primary order for one of your mapped distributors — priced
        with the distributor's PRIMARY price list, not the secondary one.
        Every primary order — however it's punched — starts as "Placed" and
        needs an ASM/manager Accept before it's final; once accepted
        there's nothing further to do on it.
      </div>

      {mappedDistributors && mappedDistributors.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="primary-field-filters">
          <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
                 className="h-9 px-2 rounded border border-neutral-200 bg-white text-neutral-900 text-xs flex-1 min-w-[110px]"
                 data-testid="primary-field-filter-date-from" />
          <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
                 className="h-9 px-2 rounded border border-neutral-200 bg-white text-neutral-900 text-xs flex-1 min-w-[110px]"
                 data-testid="primary-field-filter-date-to" />
          <Select value={filterDistId} onValueChange={setFilterDistId}>
            <SelectTrigger className="h-9 text-xs flex-1 min-w-[130px]" data-testid="primary-field-filter-dist">
              <SelectValue placeholder="Distributor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All distributors</SelectItem>
              {mappedDistributors.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterUserId} onValueChange={setFilterUserId}>
            <SelectTrigger className="h-9 text-xs flex-1 min-w-[110px]" data-testid="primary-field-filter-user">
              <SelectValue placeholder="User" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All users</SelectItem>
              {knownUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {mappedDistributors === null && (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {mappedDistributors && mappedDistributors.length === 0 && (
        <div className="text-center py-12 text-sm text-neutral-500" data-testid="primary-field-no-distributor">
          No distributor is mapped to you yet — ask your admin to map one
          before you can place a primary order.
        </div>
      )}

      {ordersLoading && mappedDistributors?.length > 0 && (
        <div className="flex items-center justify-center py-10 text-neutral-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!ordersLoading && mappedDistributors?.length > 0 && orders.length === 0 && (
        <div className="text-center py-12 text-sm text-neutral-500" data-testid="primary-field-empty">
          No primary orders placed yet.
        </div>
      )}

      <div className="space-y-2">
        {orders.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => openDetail(o.id)}
            className="w-full bg-white rounded-xl border p-3 flex items-center justify-between text-left hover:border-[#E5197F]/40"
            data-testid={`primary-field-row-${o.order_no}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium">{o.order_no}</div>
              <div className="text-[11px] text-neutral-500">
                {o.order_date} · {Number(o.total_qty_kg || 0).toFixed(1)} kg
              </div>
              {o.for_distributor_name && (
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  <span className="text-neutral-400">for</span> {o.for_distributor_name}
                  {o.for_distributor_code && ` (${o.for_distributor_code})`}
                </div>
              )}
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] border shrink-0 ${STATUS_COLORS[o.status] || "bg-neutral-100 text-neutral-600 border-neutral-200"}`}
                  data-testid={`primary-field-status-${o.order_no}`}>
              {statusLabel(o.status)}
            </span>
          </button>
        ))}
      </div>

      {/* New order form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-end md:items-center justify-center"
             data-testid="primary-field-form-dialog">
          <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl flex flex-col max-h-[92vh]">
            <div className="flex items-center justify-between p-4 pb-2 shrink-0">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#E5197F]" />
                {editingOrderId ? "Edit Primary Order" : "New Primary Order"}
              </h2>
              <button onClick={() => { setShowForm(false); setShowReview(false); setEditingOrderId(null); }}
                      className="text-sm text-neutral-500">Close</button>
            </div>

            <div className="px-4 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="text-xs text-neutral-500">Distributor</label>
                {editingOrderId ? (
                  <div className="w-full border rounded-md p-2 text-sm bg-neutral-50 text-neutral-700 mt-1">
                    {mappedDistributors?.find((d) => d.id === distributorId)?.name || distributorId}
                  </div>
                ) : (
                  <Select value={distributorId || undefined} onValueChange={onPickDistributor}>
                    <SelectTrigger className="h-10 text-sm bg-white mt-1" data-testid="primary-field-dist-select">
                      <SelectValue placeholder="Select distributor" />
                    </SelectTrigger>
                    <SelectContent>
                      {mappedDistributors?.map((d) => (
                        <SelectItem key={d.id} value={d.id} data-testid={`primary-field-dist-option-${d.code}`}>
                          <span className="font-mono text-[11px] text-neutral-500 mr-2">{d.code}</span>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {distributorId && skuInfo && !skuInfo.in_window && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  This distributor's primary-order window is currently closed.
                  Submitting will be blocked until it reopens.
                </div>
              )}

              {distributorId && (
                <div className="relative">
                  <Search className="h-4 w-4 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search SKUs…"
                    className="w-full h-9 pl-8 pr-3 rounded border border-neutral-200 text-sm"
                    data-testid="primary-field-sku-search"
                  />
                </div>
              )}

              {/* Phase 15 · "Suggest Primary Order" — one tap fills every
                  SKU's qty with (trailing 3-month avg secondary sales −
                  this month's secondary sales so far), so restocking to
                  keep pace with normal sell-through doesn't need typing
                  each line by hand. Only meaningful for the distributor's
                  own order — a manager placing on a distributor's behalf
                  doesn't get this shortcut. */}
              {distributorId && canAuto && skuInfo?.items?.some(
                (s) => s.suggested_qty_kg > 0 && (s.allowed_uoms || []).includes("KG")) && (
                <button
                  type="button"
                  onClick={() => {
                    for (const s of skuInfo.items) {
                      if (s.suggested_qty_kg > 0 && (s.allowed_uoms || []).includes("KG")) {
                        setEntry(s.sku_id, "uom", "KG");
                        setEntry(s.sku_id, "qty", String(s.suggested_qty_kg));
                      }
                    }
                  }}
                  className="w-full h-9 rounded-lg border border-[#E5197F]/40 text-[#E5197F] text-xs font-semibold"
                  data-testid="primary-field-fill-all-suggested"
                >
                  Fill all with suggested qty (avg − stock + open orders)
                </button>
              )}

              {distributorId && skuInfo === null && (
                <div className="flex items-center justify-center py-10 text-neutral-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              )}

              {distributorId && skuInfo && skuInfo.items.length === 0 && (
                <div className="text-center py-8 text-sm text-neutral-500">
                  No SKUs are orderable for this distributor right now.
                </div>
              )}

              <div className="space-y-2">
                {filteredSkus.map((s) => {
                  const en = entries[s.sku_id] || { uom: defaultUomFor(s, { excludePcs: true }), qty: "" };
                  const { qtyKg, qtyPcs } = toCanonical(s, en.uom, en.qty);
                  const hasQty = Number(en.qty) > 0;
                  const rewards = hasQty ? liveSchemeRewards(s, qtyKg, qtyPcs) : [];
                  const gapHints = hasQty ? nextTierGaps(s, qtyKg, qtyPcs) : [];
                  const hints = _hintsFor(s);
                  // Phase 15 · "Suggest Primary Order" — only meaningful for
                  // the distributor's OWN order (a manager placing on behalf
                  // of a distributor sees the plain form); the backend only
                  // sends these fields at all when they were computed.
                  const canSuggest = canAuto
                    && s.suggested_qty_kg > 0
                    && (s.allowed_uoms || []).includes("KG");
                  return (
                    <div key={s.sku_id}
                         className={"space-y-1.5 border rounded-lg p-2 " + (hasQty ? "border-[#E5197F]/40 bg-[#E5197F]/5" : "border-neutral-200")}
                         data-testid={`primary-field-sku-row-${s.sku_code}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-mono text-[11px] text-neutral-500 mr-1.5">{s.sku_code}</span>
                          <span className="text-sm text-neutral-900">{s.name || "—"}</span>
                        </div>
                        <div className="text-[10px] text-neutral-400 shrink-0">
                          ₹{s.unit_price.toFixed(2)}/pc
                        </div>
                      </div>
                      {hints.map((h, i) => (
                        <div key={h.scheme_id || i} className="text-[10px] text-[#E5197F] font-semibold"
                             data-testid={`primary-field-scheme-hint-${s.sku_code}-${i}`}>
                          🎁 Scheme: {h.text}
                        </div>
                      ))}
                      {rewards.map((r, i) => (
                        <div key={r.scheme_id || i} className="text-[10px] text-emerald-700 font-semibold"
                             data-testid={`primary-field-scheme-eligible-${s.sku_code}-${i}`}>
                          ✓ Eligible now: {r.kind === "discount" ? r.text : `${r.text} free`}
                        </div>
                      ))}
                      {gapHints.map((g, i) => (
                        <div key={i} className="text-[10px] text-amber-600 font-semibold"
                             data-testid={`primary-field-scheme-gap-${s.sku_code}-${i}`}>
                          ➕ Add {g.gap} {g.unit} more for{" "}
                          {g.reward.text}{g.reward.kind !== "discount" ? " free" : ""}
                        </div>
                      ))}
                      {canAuto && (
                        <div className="text-[10px] text-neutral-500 flex flex-wrap items-center gap-x-3 gap-y-0.5"
                             data-testid={`primary-field-suggest-${s.sku_code}`}>
                          <span>Avg/mo <b className="text-neutral-700">{(s.avg_monthly_secondary_kg ?? 0).toFixed(1)}</b></span>
                          <span>− Stock <b className="text-neutral-700">{(s.current_stock_kg ?? 0).toFixed(1)}</b></span>
                          <span>+ Open orders <b className="text-neutral-700">{(s.open_secondary_kg ?? 0).toFixed(1)}</b> kg</span>
                          {canSuggest && (
                            <button
                              type="button"
                              onClick={() => { setEntry(s.sku_id, "uom", "KG"); setEntry(s.sku_id, "qty", String(s.suggested_qty_kg)); }}
                              className="ml-auto text-[#E5197F] font-semibold underline decoration-dotted"
                              data-testid={`primary-field-use-suggested-${s.sku_code}`}
                            >
                              Suggested {s.suggested_qty_kg} kg — Use
                            </button>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <UomSelector
                          sku={s}
                          value={en.uom}
                          onChange={(u) => setEntry(s.sku_id, "uom", u)}
                          testId={`primary-field-uom-${s.sku_code}`}
                          excludePcs
                        />
                        <input
                          type="number" min="0" step="any"
                          value={en.qty}
                          onChange={(e) => setEntry(s.sku_id, "qty", e.target.value)}
                          placeholder="Qty"
                          className="w-24 h-9 px-2 rounded border border-neutral-200 text-sm text-right"
                          data-testid={`primary-field-qty-${s.sku_code}`}
                        />
                        {hasQty && (
                          <div className="text-[10px] text-neutral-500" data-testid={`primary-field-line-summary-${s.sku_code}`}>
                            {qtyKg.toFixed(1)} kg
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <textarea
                value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                rows={2}
                data-testid="primary-field-notes"
                className="w-full text-sm rounded border border-neutral-200 p-2"
              />
            </div>

            {/* Cart summary — Total Kg / Total Amount, live. */}
            <div className="p-4 pt-3 border-t border-neutral-200 shrink-0 space-y-2">
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span data-testid="primary-field-cart-lines">{cart.lineCount} SKU{cart.lineCount === 1 ? "" : "s"}</span>
                <span data-testid="primary-field-cart-kg">{cart.totalKg.toFixed(2)} kg</span>
                <span className="font-bold text-neutral-900" data-testid="primary-field-cart-amount">
                  {INR.format(cart.totalAmount)}
                </span>
              </div>
              {cart.schemeCount > 0 && (
                <div className="text-xs text-emerald-700 font-semibold" data-testid="primary-field-cart-scheme-count">
                  🎁 {cart.schemeCount} SKU{cart.schemeCount === 1 ? "" : "s"} eligible for a scheme
                </div>
              )}
              <button
                onClick={() => setShowReview(true)} disabled={submitting || cart.lineCount === 0}
                data-testid="primary-field-next-btn"
                className="w-full h-11 rounded-lg bg-[#E5197F] text-white font-medium text-sm disabled:opacity-50"
              >
                Next — review order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review order dialog — a confirm step between entry and submit so the
          rep can double-check quantities and see every scheme they've earned
          in one place before the order is actually placed. */}
      <Dialog open={showReview} onOpenChange={setShowReview}>
        <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)] [&>*]:min-w-0" data-testid="primary-field-review-dialog">
          <DialogHeader>
            <DialogTitle>Review order</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-neutral-500">
            Distributor · {mappedDistributors?.find((d) => d.id === distributorId)?.name || distributorId}
          </div>
          <div className="border rounded-lg divide-y divide-neutral-100">
            {cart.lines.map((l) => (
              <div key={l.sku.sku_id} className="p-2 text-sm"
                   data-testid={`primary-field-review-line-${l.sku.sku_code}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 truncate">
                    <span className="font-mono text-[11px] text-neutral-500 mr-1.5">{l.sku.sku_code}</span>
                    <span className="text-neutral-900">{l.sku.name || "—"}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="whitespace-nowrap">{l.qtyKg.toFixed(1)} kg</div>
                    <div className="text-[11px] text-neutral-500 whitespace-nowrap">{INR.format(l.lineAmount)}</div>
                  </div>
                </div>
                {l.rewards.map((r, i) => (
                  <div key={r.scheme_id || i} className="text-[11px] text-emerald-700 font-semibold mt-0.5 truncate"
                       data-testid={`primary-field-review-reward-${l.sku.sku_code}-${i}`}>
                    🎁 {r.kind === "discount" ? r.text : `${r.text} free`}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-neutral-200 px-3 py-2 space-y-1 text-xs">
            <div className="flex items-center justify-between gap-2 text-neutral-500">
              <span className="shrink-0">{cart.lineCount} SKU{cart.lineCount === 1 ? "" : "s"}</span>
              <span className="shrink-0">Total Kg</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold text-neutral-900 shrink-0 whitespace-nowrap" data-testid="primary-field-review-total">
                {INR.format(cart.totalAmount)}
              </span>
              <span className="font-semibold text-neutral-900 shrink-0 whitespace-nowrap" data-testid="primary-field-review-total-kg">
                {cart.totalKg.toFixed(2)} kg
              </span>
            </div>
            {cart.schemeCount > 0 && (
              <div className="text-emerald-700 font-semibold pt-1">
                🎁 {cart.schemeCount} SKU{cart.schemeCount === 1 ? "" : "s"} eligible for a scheme
              </div>
            )}
            {freeItemTotals(cart.lines).map(([label, count]) => (
              <div key={label} className="flex items-center justify-between gap-2 text-emerald-700 font-semibold"
                   data-testid={`primary-field-review-free-item-total-${label.replace(/\W+/g, "-")}`}>
                <span className="min-w-0 truncate">🎁 Total {label}</span>
                <span className="shrink-0">{count}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowReview(false)}
              className="flex-1 h-11 rounded-lg border border-neutral-200 text-sm font-medium"
              data-testid="primary-field-review-back-btn"
            >
              Back
            </button>
            <button
              onClick={submit} disabled={submitting}
              className="flex-1 h-11 rounded-lg bg-[#E5197F] text-white font-medium text-sm disabled:opacity-50"
              data-testid="primary-field-review-confirm-btn"
            >
              {submitting
                ? <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                : editingOrderId ? "Confirm & save" : "Confirm & place order"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order detail dialog */}
      <Dialog open={!!viewOrder} onOpenChange={(v) => {
        if (!v) { setViewOrder(null); setRejectReasonOpen(false); setRejectReason(""); }
      }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="primary-field-detail-dialog">
          <DialogHeader>
            <DialogTitle>{viewOrder?.order_no || "Primary order"}</DialogTitle>
          </DialogHeader>
          {viewLoading ? (
            <div className="flex items-center justify-center py-10 text-neutral-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : viewOrder && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] border ${STATUS_COLORS[viewOrder.status] || "bg-neutral-100 text-neutral-600 border-neutral-200"}`}>
                  {statusLabel(viewOrder.status)}
                </span>
                <span className="text-sm font-bold shrink-0 whitespace-nowrap">{INR.format(viewOrder.net_amount || 0)}</span>
              </div>
              <div className="text-xs text-neutral-500">
                {viewOrder.order_date}
              </div>
              {viewOrder.for_distributor_name && (
                <div className="text-xs text-neutral-700" data-testid="primary-field-detail-distributor">
                  <span className="text-neutral-400">Distributor · </span>
                  {viewOrder.for_distributor_name}
                  {viewOrder.for_distributor_code && ` (${viewOrder.for_distributor_code})`}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 text-xs text-neutral-500">
                <span className="shrink-0">Total order qty</span>
                <span className="font-semibold text-neutral-900 shrink-0 whitespace-nowrap" data-testid="primary-field-detail-total-kg">
                  {Number(viewOrder.total_qty_kg || 0).toFixed(2)} kg
                </span>
              </div>
              <div className="border rounded-lg divide-y divide-neutral-100">
                {(viewOrder.lines || []).map((l) => (
                  <div key={l.id} className="p-2 flex items-center justify-between text-sm"
                       data-testid={`primary-field-detail-line-${l.sku_code_snapshot}`}>
                    <div className="min-w-0">
                      <div className="font-mono text-[11px] text-neutral-500">{l.sku_code_snapshot}</div>
                      <div className="text-neutral-900 truncate">
                        {l.sku_name_snapshot || "—"}
                      </div>
                      {l.scheme_code_snapshot && (
                        <div className="mt-0.5" data-testid={`primary-field-detail-line-scheme-${l.sku_code_snapshot}`}>
                          <span className="inline-flex max-w-full items-center rounded-full bg-pink-100 text-[#E5197F] px-1.5 py-0.5 text-[10px] font-semibold truncate">
                            {l.scheme_code_snapshot}
                          </span>
                        </div>
                      )}
                      {l.discount_amount > 0 && (
                        <div className="text-[11px] text-emerald-700">
                          Scheme disc: −{INR.format(l.discount_amount)}
                        </div>
                      )}
                      {l.scheme_benefit_type === "free_item_note" && l.scheme_benefit_value && (
                        <div className="text-[11px] text-emerald-700"
                             data-testid={`primary-field-detail-line-free-item-${l.sku_code_snapshot}`}>
                          🎁 Free: {l.scheme_benefit_value}
                        </div>
                      )}
                      {l.fulfilled_qty_pcs > 0 && (
                        <div className="text-[11px] text-emerald-700"
                             data-testid={`primary-field-detail-line-fulfilled-${l.sku_code_snapshot}`}>
                          ✓ Fulfilled {Number(l.fulfilled_qty_kg || 0).toFixed(1)} kg
                          {l.qty_kg > 0 && ` of ${Number(l.qty_kg).toFixed(1)} kg`}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0 ml-2 whitespace-nowrap">
                      <div>{Number(l.qty_kg || 0).toFixed(1)} kg</div>
                      <div className="text-[11px] text-neutral-500">{INR.format(l.line_total || 0)}</div>
                    </div>
                  </div>
                ))}
                {(!viewOrder.lines || viewOrder.lines.length === 0) && (
                  <div className="p-3 text-xs text-neutral-400 text-center">No lines</div>
                )}
              </div>
              <div className="rounded-lg border border-neutral-200 px-3 py-2 space-y-1 text-xs">
                <div className="flex items-center justify-between gap-2 text-neutral-500">
                  <span className="shrink-0">Subtotal (Gross)</span>
                  <span className="shrink-0 whitespace-nowrap">{INR.format(viewOrder.gross_amount || 0)}</span>
                </div>
                {viewOrder.scheme_discount_amount > 0 && (
                  <div className="flex items-center justify-between gap-2 text-emerald-700">
                    <span className="shrink-0">Scheme discount</span>
                    <span className="shrink-0 whitespace-nowrap">−{INR.format(viewOrder.scheme_discount_amount)}</span>
                  </div>
                )}
                {freeItemTotalsFromOrderLines(viewOrder.lines).map(([label, count]) => (
                  <div key={label} className="flex items-center justify-between gap-2 text-emerald-700"
                       data-testid={`primary-field-detail-free-item-total-${label.replace(/\W+/g, "-")}`}>
                    <span className="min-w-0 truncate">🎁 Total {label}</span>
                    <span className="shrink-0">{count}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-2 font-semibold text-neutral-900 pt-1 border-t border-neutral-100">
                  <span className="shrink-0">Net</span>
                  <span className="shrink-0 whitespace-nowrap" data-testid="primary-field-detail-net">{INR.format(viewOrder.net_amount || 0)}</span>
                </div>
              </div>
              {viewOrder.notes && (
                <div className="text-xs text-neutral-500">Notes: {viewOrder.notes}</div>
              )}
              <button
                onClick={() => downloadPdf(viewOrder)}
                disabled={pdfBusy}
                data-testid="primary-field-download-pdf-btn"
                className="w-full h-10 rounded-lg border border-neutral-200 text-neutral-700 font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download PDF
              </button>
              {viewOrder.status === "placed" && (
                <button
                  onClick={() => openEditForm(viewOrder)}
                  data-testid="primary-field-edit-btn"
                  className="w-full h-10 rounded-lg border border-neutral-200 text-neutral-700 font-medium text-sm flex items-center justify-center gap-2"
                >
                  <Pencil className="w-4 h-4" /> Edit order
                </button>
              )}
              {viewOrder.status === "placed" && canAccept && (
                <button
                  onClick={() => acceptOrder(viewOrder.id)}
                  disabled={accepting}
                  data-testid="primary-field-accept-btn"
                  className="w-full h-11 rounded-lg bg-emerald-600 text-white font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Accept order
                </button>
              )}
              {viewOrder.status === "placed" && canAccept && !rejectReasonOpen && (
                <button
                  onClick={() => setRejectReasonOpen(true)}
                  data-testid="primary-field-reject-open-btn"
                  className="w-full h-9 rounded-lg text-rose-600 font-medium text-xs flex items-center justify-center gap-1"
                >
                  <XCircle className="w-3.5 h-3.5" /> Reject order
                </button>
              )}
              {viewOrder.status === "placed" && canAccept && rejectReasonOpen && (
                <div className="space-y-2 border border-rose-200 bg-rose-50 rounded-lg p-2">
                  <textarea
                    value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for rejecting (min 3 characters)…"
                    rows={2}
                    data-testid="primary-field-reject-reason"
                    className="w-full text-sm rounded border border-rose-200 p-2"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setRejectReasonOpen(false); setRejectReason(""); }}
                      className="flex-1 h-9 rounded-lg border border-neutral-200 text-neutral-600 text-xs font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => rejectOrder(viewOrder.id)}
                      disabled={rejecting}
                      data-testid="primary-field-reject-confirm-btn"
                      className="flex-1 h-9 rounded-lg bg-rose-600 text-white text-xs font-medium disabled:opacity-50 flex items-center justify-center gap-1"
                    >
                      {rejecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                      Confirm reject
                    </button>
                  </div>
                </div>
              )}
              {viewOrder.status === "placed" && !canAccept && (
                <div className="text-xs text-neutral-500 text-center py-1">
                  Awaiting your ASM/manager's acceptance.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
